import React, { useEffect, useRef } from 'react';
import { bindActionCreators } from 'redux';
import { connect, useDispatch, useSelector } from 'react-redux';

import { library } from '@fortawesome/fontawesome-svg-core';
import { faAsterisk, faPaperclip, faHome, faEnvelope, faExclamation, faLock, faPhone, faTimes } from '@fortawesome/free-solid-svg-icons';
library.add(faAsterisk, faPaperclip, faHome, faEnvelope, faExclamation, faLock, faPhone, faTimes);

// Components

import Ssap from '@components/ssap/Ssap';

// ActionCreators

import {
    adjustFlow,
    clearSsapState,
    setFlow,
    setLinkFrom,
    fetchApplication,
    setSsapLaunch,
} from '@actions/ssap/ssapActionCreators';

import { hideModal, showModal } from '@actions/common/actionCreators';

// Selectors

import {
    getServerDate,
    getSsapLaunchData,
    selectIfExtraFinalSummaryShown,
    selectSettings
} from '@reducers/appReducer';

// Hooks

import { useLocation } from '@hooks/useLocation.ts';

// Utils

import { isFormInInitialState, isLeaveModalShown } from '@reducers/appReducer';

// Constants

import { configs } from '@constants/configs/configs';
import { flowTypes as FLOW_TYPES } from '@constants/ssap/flows/flowTypes';
import { getCachedFlows } from '@constants/ssap/flows/pagesList';
import { useNavigate } from 'react-router-dom';

const SsapContainer = props => {

    const {
        adjustedFlow,
        data: {
            applyingForFinancialAssistanceIndicator,
            coverageYear,
            ssapApplicationId,
        },
        flow: {
            flowType,
            isFlowLoaded
        },
        householdMembers,
        householdMembers: {
            length: hhmsQty
        },
        isSsapFormsInInitialsState,
        isLeaveModalShown,
        page,
        fromCAP
    } = props;

    const navigate = useNavigate();
    const isExtraFinalSummaryShown = useSelector(selectIfExtraFinalSummaryShown);
    const serverDate = useSelector(getServerDate);
    const settings = useSelector(selectSettings);

    const {
        watchForDuplications
    } = useSelector(getSsapLaunchData);
    const dispatch = useDispatch();

    const SSAP_CHANNEL_NAME = 'SSAP_CHANNEL';
    const SSAP_CHANNEL_ONLINE_MESSAGE = 'SSAP_OPEN';
    const SSAP_CHANNEL_DUPLICATION_MESSAGE = 'SSAP_DUPLICATION_DETECTED';

    const channel = new BroadcastChannel(SSAP_CHANNEL_NAME);

    const isOriginalTab = useRef(false);

    useEffect(() => {
        const handleMessage = (msg) => {
            if (msg.data === SSAP_CHANNEL_ONLINE_MESSAGE) {
                isOriginalTab.current = true;
                channel.postMessage(SSAP_CHANNEL_DUPLICATION_MESSAGE);
            }
            if (!isOriginalTab.current && msg.data === SSAP_CHANNEL_DUPLICATION_MESSAGE) {
                navigate(configs.routes.FALLBACK, { state:{ isSsapDuplication: true } });
            }
        };

        if (watchForDuplications) {
            channel.addEventListener('message', handleMessage);
            channel.postMessage(SSAP_CHANNEL_ONLINE_MESSAGE);
        }

        return () => {
            props.actions.clearSsapState();
            channel.removeEventListener('message', handleMessage);
        };
    }, []);

    useEffect(() => {
        if(isFlowLoaded) {
            if(flowType === FLOW_TYPES.NON_FINANCIAL_FLOW_NON_SWITCHABLE) {
                return undefined;
            }
            if(applyingForFinancialAssistanceIndicator) {
                flowType !== FLOW_TYPES.FINANCIAL_FLOW && props.actions.setFlow(FLOW_TYPES.FINANCIAL_FLOW);
            } else {
                flowType !== FLOW_TYPES.NON_FINANCIAL_FLOW_SWITCHABLE && props.actions.setFlow(FLOW_TYPES.NON_FINANCIAL_FLOW_SWITCHABLE);
            }
        }
    }, [ applyingForFinancialAssistanceIndicator ]);

    const hhmDatesOfBirth = householdMembers.map(hhm => hhm.dateOfBirth).toString();

    useEffect(() => {
        props.actions.adjustFlow(
            getCachedFlows({
                flowType,
                hhmsQty,
                householdMembers,
                coverageYear,
                isExtraFinalSummaryShown,
                serverDate,
                settings
            })
        );
    }, [
        flowType,
        hhmsQty,
        hhmDatesOfBirth,
        coverageYear
    ]);

    const {
        fromMyApp
    } = useLocation();

    useEffect(() => {
        props.actions.fetchApplication();
        if (fromCAP) {
            const encHouseHoldId = sessionStorage.getItem('encHouseHoldId') ?? 'encID';
            dispatch(setSsapLaunch({
                backLinkText: 'common.backToApplication',
                backUrl: configs.routes.CAP_APPLICATIONS.replace(':id', encHouseHoldId),
                consumer: 'CAP',
                loadBy: 'caseNumber',
                watchForDuplications: false
            }));
        }
    }, []);

    useEffect(() => {
        if (fromMyApp) {
            props.actions.setLinkFrom('MyApplications');
        }
    }, [fromMyApp]);

    useEffect(() => {
        if(!isSsapFormsInInitialsState && !isLeaveModalShown) {
            global.onbeforeunload = () => {
                return '';
            };
        } else {
            global.onbeforeunload = null;
        }
        return () => {
            global.onbeforeunload = null;
        };
    });

    return (ssapApplicationId && adjustedFlow[page])
        ? <Ssap {...{ history }} />
        : <div className='pre_load_placeholder'/>;
};

const mapStateToProps = state => ({
    adjustedFlow: state.ssap.adjustedFlow,
    data: state.ssap.data,
    flow: state.ssap.flow,

    householdMembers: state
        .ssap
        .data
        .taxHousehold
        .householdMember,

    isLeaveModalShown: isLeaveModalShown(state),
    isSsapFormsInInitialsState: isFormInInitialState(state),
    page: state.ssap.page
});

const mapDispatchToProps = dispatch => ({
    actions: bindActionCreators({
        adjustFlow,
        clearSsapState,
        fetchApplication,
        hideModal,
        setFlow,
        setLinkFrom,
        showModal
    }, dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(SsapContainer);
